import React, { useCallback, useEffect, useState } from 'react';
import { Clapperboard, RefreshCw, PlayCircle } from 'lucide-react';
import { toast } from '../ui/sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_BADGE = {
  completed: 'border border-emerald-400/30 bg-emerald-500/10 text-emerald-300',
  failed: 'border border-red-400/30 bg-red-500/10 text-red-300',
  processing: 'border border-amber-400/20 bg-amber-500/10 text-amber-200',
};

export const VideoCampaignsPanel = ({ authFetch }) => {
  const [data, setData] = useState(null);
  const [form, setForm] = useState({ title: '', template_id: '', client_id: '', service_id: 'video_comercial', text: '', narration: '' });
  const [busy, setBusy] = useState(false);
  const [refreshingId, setRefreshingId] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await authFetch(`${API}/admin/video-campaigns`);
      const json = await res.json();
      if (!res.ok) throw new Error(json.detail || 'Error cargando campañas');
      setData(json);
      setForm((prev) => ({ ...prev, template_id: prev.template_id || json.templates?.[0]?.id || '' }));
    } catch (err) {
      toast.error(err.message);
    }
  }, [authFetch]);

  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.template_id) return toast.error('Selecciona una plantilla');
    setBusy(true);
    try {
      const res = await authFetch(`${API}/admin/video-campaigns`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: form.title || 'Vídeo de campaña',
          template_id: form.template_id,
          client_id: form.client_id || null,
          service_id: form.service_id,
          modifications: form.text ? { 'Name.text': form.text } : {},
          narration_text: form.narration || '',
        }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.detail || 'No se pudo crear el vídeo');
      toast.success(json.campaign?.credits_charged ? `Vídeo lanzado · ${json.campaign.credits_charged} créditos descontados` : 'Vídeo lanzado en Creatomate');
      setForm((prev) => ({ ...prev, title: '', text: '', narration: '' }));
      load();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  const refresh = async (id) => {
    setRefreshingId(id);
    try {
      const res = await authFetch(`${API}/admin/video-campaigns/${id}/refresh`, { method: 'POST' });
      const json = await res.json();
      if (!res.ok) throw new Error(json.detail || 'Error actualizando');
      setData((prev) => ({ ...prev, campaigns: prev.campaigns.map((c) => (c.id === id ? json.campaign : c)) }));
    } catch (err) {
      toast.error(err.message);
    } finally {
      setRefreshingId(null);
    }
  };

  const toggleFeature = async (id) => {
    try {
      const res = await authFetch(`${API}/admin/video-campaigns/${id}/feature`, { method: 'POST' });
      const json = await res.json();
      if (!res.ok) throw new Error(json.detail || 'Error');
      toast.success(json.featured ? 'Vídeo destacado en la galería pública' : 'Vídeo retirado de la galería');
      setData((prev) => ({ ...prev, campaigns: prev.campaigns.map((c) => (c.id === id ? { ...c, featured: json.featured } : c)) }));
    } catch (err) {
      toast.error(err.message);
    }
  };

  const inputCls = 'w-full rounded-xl border border-border-subtle bg-background/60 px-4 py-2.5 text-sm text-text-body focus:border-primary focus:outline-none';

  return (
    <div className="space-y-6" data-testid="video-campaigns-panel">
      <form onSubmit={submit} className="glass-card space-y-4 p-6" data-testid="video-campaign-form">
        <div className="flex items-center gap-2">
          <Clapperboard className="h-5 w-5 text-primary" />
          <h3 className="text-lg font-bold text-text-heading">Nuevo vídeo de campaña (Creatomate)</h3>
        </div>
        <div className="grid gap-4 md:grid-cols-2">
          <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Título interno (ej. Bienvenida Clínica Sonrisa)" className={inputCls} data-testid="video-campaign-title-input" />
          <select value={form.template_id} onChange={(e) => setForm({ ...form, template_id: e.target.value })} className={inputCls} data-testid="video-campaign-template-select">
            {(data?.templates || []).map((t) => <option key={t.id} value={t.id}>{t.name || t.id}</option>)}
          </select>
          <input value={form.text} onChange={(e) => setForm({ ...form, text: e.target.value })} placeholder="Texto personalizado del vídeo (ej. nombre del cliente)" className={inputCls} data-testid="video-campaign-text-input" />
          <input value={form.narration} onChange={(e) => setForm({ ...form, narration: e.target.value })} placeholder="Narración con voz de Aynara (opcional, ej. guion corto en español)" className={inputCls} data-testid="video-campaign-narration-input" />
          <select value={form.client_id} onChange={(e) => setForm({ ...form, client_id: e.target.value })} className={inputCls} data-testid="video-campaign-client-select">
            <option value="">Sin facturar (interno)</option>
            {(data?.clients || []).map((c) => <option key={c.id} value={c.id}>{c.name}{c.company ? ` · ${c.company}` : ''} — {c.credits_balance} cr</option>)}
          </select>
          {form.client_id && (
            <select value={form.service_id} onChange={(e) => setForm({ ...form, service_id: e.target.value })} className={inputCls} data-testid="video-campaign-service-select">
              {(data?.video_catalog || []).map((s) => <option key={s.service_id} value={s.service_id}>{s.name} — {s.price_credits} cr (+20% gestión)</option>)}
            </select>
          )}
        </div>
        <button type="submit" disabled={busy} className="btn-primary disabled:opacity-60" data-testid="video-campaign-submit-btn">
          <PlayCircle className="h-4 w-4" /> {busy ? 'Lanzando…' : 'Generar vídeo'}
        </button>
      </form>

      <div className="space-y-3" data-testid="video-campaigns-list">
        {(data?.campaigns || []).length === 0 && <p className="text-sm text-text-muted">Aún no hay vídeos de campaña.</p>}
        {(data?.campaigns || []).map((c) => (
          <div key={c.id} className="glass-card flex flex-wrap items-center gap-4 p-4" data-testid={`video-campaign-row-${c.id}`}>
            {c.snapshot_url && <img src={c.snapshot_url} alt={c.title} className="h-14 w-24 rounded-lg object-cover" />}
            <div className="min-w-[180px] flex-1">
              <p className="text-sm font-semibold text-text-heading">{c.title}</p>
              <p className="text-xs text-text-muted">{c.template_name || c.template_id}{c.credits_charged ? ` · ${c.credits_charged} cr facturados` : ' · interno'}</p>
            </div>
            <span className={`rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-wider ${STATUS_BADGE[c.status] || STATUS_BADGE.processing}`}>{c.status}</span>
            {c.status === 'completed' && (
              <button type="button" onClick={() => toggleFeature(c.id)} className={`rounded-full border px-3 py-1.5 text-xs font-semibold transition ${c.featured ? 'border-amber-400/60 bg-amber-400/15 text-amber-300' : 'border-border-subtle text-text-muted hover:border-amber-400/50 hover:text-amber-300'}`} data-testid={`video-campaign-feature-${c.id}`}>
                {c.featured ? '⭐ En galería pública' : '☆ Destacar en web'}
              </button>
            )}
            {c.status === 'completed' && c.video_url && (
              <a href={c.video_url} target="_blank" rel="noopener noreferrer" className="text-sm font-semibold text-primary hover:underline" data-testid={`video-campaign-link-${c.id}`}>Ver vídeo</a>
            )}
            {c.status !== 'completed' && (
              <button type="button" onClick={() => refresh(c.id)} disabled={refreshingId === c.id} className="flex items-center gap-1 rounded-full border border-border-subtle px-3 py-1.5 text-xs text-text-muted hover:border-primary hover:text-primary disabled:opacity-50" data-testid={`video-campaign-refresh-${c.id}`}>
                <RefreshCw className={`h-3.5 w-3.5 ${refreshingId === c.id ? 'animate-spin' : ''}`} /> Actualizar
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default VideoCampaignsPanel;
